import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Search, LayoutGrid, List, Filter } from "lucide-react";
import { toast } from "sonner";
import {
  api,
  SOURCES,
  SOURCE_LABEL,
  EDUCATION_LEVELS,
  CAREER_GOALS,
  formatDateIST,
} from "../lib/api";
import { useLookups } from "../lib/lookups";
import { LeadCard } from "../components/LeadCard";
import { Pill } from "../components/Pill";
import { SourceBadge } from "../components/SourceBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import DateRangePicker from "../components/DateRangePicker";
import MultiSelectFilter from "../components/MultiSelectFilter";
import ColumnPicker, { COLUMN_CATALOG, loadColumns } from "../components/ColumnPicker";
import { Columns3, RefreshCw } from "lucide-react";

function isOverdue(iso) {
  if (!iso) return false;
  return new Date(iso) < new Date();
}

function NewLeadModal({ open, onClose, onCreated }) {
  const { COURSES } = useLookups();
  const [form, setForm] = useState({
    source: "offline",
    name: "",
    email: "",
    phone: "",
    course_interest: "Digital Marketing",
    education_level: "",
    career_goal: "",
    budget: "",
    notes: "",
    source_channel: "",
    referred_by: "",
    campaign_name: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      const { data } = await api.post("/leads", form);
      toast.success("Lead created");
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="new-lead-modal">
        <DialogHeader>
          <DialogTitle>Add Lead Manually</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select label="Source" value={form.source} onChange={(v) => set("source", v)} options={SOURCES.map((s) => ({ value: s, label: SOURCE_LABEL[s] }))} testid="newlead-source" />
          <Input label="Full Name *" value={form.name} onChange={(v) => set("name", v)} testid="newlead-name" />
          <Input label="Email" value={form.email} onChange={(v) => set("email", v)} testid="newlead-email" />
          <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="newlead-phone" />
          <Select label="Course Interest" value={form.course_interest} onChange={(v) => set("course_interest", v)} options={COURSES.map((c) => ({ value: c, label: c }))} testid="newlead-course" />
          <Select label="Education" value={form.education_level} onChange={(v) => set("education_level", v)} options={[{ value: "", label: "—" }, ...EDUCATION_LEVELS.map((e) => ({ value: e, label: e }))]} testid="newlead-education" />
          <Select label="Career Goal" value={form.career_goal} onChange={(v) => set("career_goal", v)} options={[{ value: "", label: "—" }, ...CAREER_GOALS.map((e) => ({ value: e, label: e }))]} testid="newlead-career" />
          <Input label="Budget" value={form.budget} onChange={(v) => set("budget", v)} testid="newlead-budget" />
          {form.source === "offline" && (
            <>
              <Select label="Channel" value={form.source_channel} onChange={(v) => set("source_channel", v)} options={["", "Walk-in", "Referral", "WhatsApp", "Event", "Phone Call"].map((e) => ({ value: e, label: e || "—" }))} testid="newlead-channel" />
              <Input label="Referred By" value={form.referred_by} onChange={(v) => set("referred_by", v)} testid="newlead-referredby" />
            </>
          )}
          {form.source === "google" && (
            <Select label="Channel" value={form.source_channel} onChange={(v) => set("source_channel", v)} options={["", "Search", "Display", "YouTube"].map((e) => ({ value: e, label: e || "—" }))} testid="newlead-gchannel" />
          )}
          {(form.source.startsWith("meta") || form.source === "google") && (
            <Input label="Campaign Name" value={form.campaign_name} onChange={(v) => set("campaign_name", v)} testid="newlead-campaign" />
          )}
          <div className="md:col-span-2">
            <label className="text-xs text-[#6B7280]">Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 text-sm bg-white" data-testid="newlead-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-[8px] border border-[#6C47FF] text-sm font-[600] min-h-[44px]">Cancel</button>
          <button onClick={submit} data-testid="newlead-submit" className="px-4 py-2 rounded-[8px] btn-mx text-sm font-[600] min-h-[44px]">Create</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-[#6B7280]">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white" />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs text-[#6B7280]">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white">
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

const FILTERS_KEY = "lms_leads_filters_v2";
const RANGE_KEY = "lms_leads_range_v2";
const DEFAULT_FILTERS = { category: [], stage: [], course: [], source: [], q: "" };
const DEFAULT_RANGE = { from: null, to: null };
function loadStored(key, fb) {
  try { const raw = sessionStorage.getItem(key); return raw ? JSON.parse(raw) : fb; } catch { return fb; }
}

export default function Leads() {
  const [view, setView] = useState("board");
  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const openLeadInNewTab = useCallback((lead) => {
    const id = typeof lead === "string" ? lead : lead?.id;
    if (!id) return;
    window.open(`/leads/${id}`, "_blank", "noopener,noreferrer");
  }, []);
  const [showNew, setShowNew] = useState(false);
  const [filters, setFilters] = useState(() => loadStored(FILTERS_KEY, DEFAULT_FILTERS));
  const [range, setRange] = useState(() => loadStored(RANGE_KEY, DEFAULT_RANGE));
  const [sortOrder, setSortOrder] = useState("desc");
  const [dragged, setDragged] = useState(null);
  const { CATEGORIES, STAGES, CATEGORY_COLORS, STAGE_COLORS, COURSES } = useLookups();

  useEffect(() => { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); }, [filters]);
  useEffect(() => { sessionStorage.setItem(RANGE_KEY, JSON.stringify(range)); }, [range]);

  const load = useCallback(async () => {
    const params = {};
    if (filters.category.length) params.category = filters.category;
    if (filters.stage.length) params.stage = filters.stage;
    if (filters.course.length) params.course = filters.course;
    if (filters.source.length) params.source = filters.source;
    if (filters.q) params.q = filters.q;
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    const { data } = await api.get("/leads", { params });
    setLeads(data);
  }, [filters, range]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/leads").then((r) => setTotalCount(r.data.length)).catch(() => {});
  }, []);

  const filtersActive = useMemo(
    () => filters.category.length + filters.stage.length + filters.course.length + filters.source.length > 0
      || !!filters.q || !!range.from || !!range.to,
    [filters, range]
  );

  const clearFilters = () => { setFilters(DEFAULT_FILTERS); setRange(DEFAULT_RANGE); };

  const [refreshing, setRefreshing] = useState(false);
  const refreshLeads = async () => {
    setRefreshing(true);
    try {
      const kinds = ["meta", "google", "whatsapp", "offline"];
      const results = await Promise.all(
        kinds.map((k) =>
          api.post(`/sync/run?source_kind=${k}`).then((r) => ({ k, ...r.data })).catch((e) => ({ k, error: e.message }))
        )
      );
      const totals = results.reduce(
        (acc, r) => ({
          inserted: acc.inserted + (r.inserted || 0),
          skipped: acc.skipped + (r.skipped || 0),
          already: acc.already + (r.already_synced || 0),
          issues: acc.issues + (r.issues_count || 0),
        }),
        { inserted: 0, skipped: 0, already: 0, issues: 0 }
      );
      const anySynced = results.some((r) => r.synced_at);
      if (anySynced) {
        toast.success(`Synced: +${totals.inserted} new · ${totals.skipped} duplicates skipped · ${totals.already} already synced`);
      } else {
        toast.success("Leads refreshed");
      }
      await load();
      api.get("/leads").then((r) => setTotalCount(r.data.length)).catch(() => {});
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  // Column preferences (localStorage)
  const [columns, setColumns] = useState(() => loadColumns());
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const visibleColumns = useMemo(() => columns.filter((c) => c.visible).map((c) => c.key), [columns]);
  const COL_LABEL = useMemo(() => Object.fromEntries(COLUMN_CATALOG.map((c) => [c.key, c.label])), []);

  const onDragStart = (e, lead) => {
    setDragged(lead);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = async (stage) => {
    if (!dragged || dragged.stage === stage) return;
    setLeads((prev) => prev.map((l) => (l.id === dragged.id ? { ...l, stage } : l)));
    setDragged(null);
    try {
      await api.patch(`/leads/${dragged.id}`, { stage });
    } catch {
      toast.error("Failed to update stage");
      load();
    }
  };

  const advanceStage = async (lead, nextStage) => {
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, stage: nextStage } : l)));
    try {
      await api.patch(`/leads/${lead.id}`, { stage: nextStage });
      toast.success(`Moved to ${nextStage}`);
    } catch {
      toast.error("Failed to move stage");
      load();
    }
  };

  const nextStageFor = (stage) => {
    if (stage === "Enrolled" || stage === "Not Interested") return null;
    const idx = STAGES.indexOf(stage);
    if (idx === -1 || idx >= STAGES.length - 1) return null;
    return STAGES[idx + 1];
  };

  const quickActionsFor = (stage) => {
    if (stage === "Fees Shared") {
      return [
        { label: "✓ Enrolled", stage: "Enrolled", variant: "win" },
        { label: "✕ Not Int.", stage: "Not Interested", variant: "lose" },
      ];
    }
    return null;
  };

  const byStage = useMemo(() => {
    const map = {};
    STAGES.forEach((s) => (map[s] = []));
    const catOrder = Object.fromEntries(CATEGORIES.map((c, i) => [c, i]));
    leads.forEach((l) => {
      if (map[l.stage]) map[l.stage].push(l);
    });
    // Parse created_at safely — ISO string or falsy. Missing/blank pushed to end.
    const ts = (l) => {
      const v = l.created_at;
      if (!v) return sortOrder === "asc" ? Infinity : -Infinity;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? (sortOrder === "asc" ? Infinity : -Infinity) : t;
    };
    const cmpDate = (a, b) => {
      if (sortOrder === "asc") return ts(a) - ts(b);        // oldest first
      return ts(b) - ts(a);                                  // newest first
    };
    // Sort by date first; use category order only as a stable tiebreaker for
    // leads created at the exact same millisecond.
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => cmpDate(a, b) || ((catOrder[a.category] ?? 99) - (catOrder[b.category] ?? 99)))
    );
    return map;
  }, [leads, STAGES, CATEGORIES, sortOrder]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-[800] text-[#0D0B1F] leading-tight">Leads</h1>
          <div className="text-[13px] text-[#8B90A0] mt-0.5">Manage your lead pipeline</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-[8px] border border-[#E8EAEF] overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setView("board")}
              data-testid="view-board"
              className={`px-3 py-2 text-sm font-[600] min-h-[44px] flex items-center gap-1 ${view === "board" ? "btn-mx" : "bg-white text-[#1A1A2E]"}`}
            >
              <LayoutGrid className="h-4 w-4" /> Board
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={`px-3 py-2 text-sm font-[600] min-h-[44px] flex items-center gap-1 ${view === "list" ? "btn-mx" : "bg-white text-[#1A1A2E]"}`}
            >
              <List className="h-4 w-4" /> List
            </button>
          </div>
          <button
            onClick={refreshLeads}
            disabled={refreshing}
            data-testid="refresh-leads-btn"
            title="Sync linked Google Sheets and reload"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-[8px] border border-[#6C47FF] bg-white text-sm font-[600] min-h-[44px] text-[#1A1A2E] disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="add-lead-btn"
            className="btn-mx rounded-[8px] px-4 py-2 text-sm font-[600] min-h-[44px] inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> Add Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E8EAEF] rounded-[12px] p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              placeholder="Search name, email, phone..."
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="search-input"
              className="w-full rounded-[6px] border border-[#E8EAEF] pl-9 pr-3 py-2 min-h-[44px] text-sm bg-white"
            />
          </div>
          <MultiSelectFilter
            label="All Lead Stages" kind="Lead Stage" testid="filter-category"
            options={CATEGORIES}
            value={filters.category}
            onChange={(v) => setFilters({ ...filters, category: v })}
          />
          <MultiSelectFilter
            label="All Sub-Stages" kind="Sub-Stage" testid="filter-stage"
            options={STAGES}
            value={filters.stage}
            onChange={(v) => setFilters({ ...filters, stage: v })}
          />
          <MultiSelectFilter
            label="All Courses" kind="Courses" testid="filter-course"
            options={COURSES}
            value={filters.course}
            onChange={(v) => setFilters({ ...filters, course: v })}
          />
          <MultiSelectFilter
            label="All Sources" kind="Sources" testid="filter-source"
            options={SOURCES.map((s) => ({ value: s, label: SOURCE_LABEL[s] }))}
            value={filters.source}
            onChange={(v) => setFilters({ ...filters, source: v })}
          />
          <DateRangePicker value={range} onChange={setRange} testid="leads-range" />
        </div>
      </div>

      {view === "board" ? (
        <>
          <div className="flex items-center justify-end" data-testid="kanban-sort-row">
            <label className="text-xs text-[#6B7280] mr-2 font-[600]">Sort cards by date:</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              data-testid="kanban-sort"
              className="rounded-[6px] border border-[#E8EAEF] px-2 py-2 min-h-[36px] text-sm bg-white"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
          <div className="kanban-scroll flex gap-4 overflow-x-auto pb-3" data-testid="kanban-board">
          {STAGES.map((stage) => (
            <div
              key={stage}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(stage)}
              className="bg-white rounded-[14px] min-w-[280px] w-[280px] overflow-hidden flex flex-col"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderTop: `3px solid ${STAGE_COLORS[stage] || "#6C47FF"}` }}
              data-testid={`kanban-column-${stage.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-[12px] font-[700] text-[#0D0B1F]">{stage}</span>
                <span className="text-[11px] font-[700] text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] || "#6C47FF" }}>{byStage[stage].length}</span>
              </div>
              <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto px-3 pb-3 pt-1">
                {byStage[stage].map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onClick={openLeadInNewTab}
                    onDragStart={onDragStart}
                    onAdvance={advanceStage}
                    nextStage={nextStageFor(lead.stage)}
                    quickActions={quickActionsFor(lead.stage)}
                    overdue={isOverdue(lead.next_followup_at)}
                  />
                ))}
                {byStage[stage].length === 0 && <div className="text-[12px] text-[#8B90A0] text-center py-8">No leads</div>}
              </div>
            </div>
          ))}
        </div>
        </>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2" data-testid="list-summary-row">
            <div className="text-sm text-[#6B7280]" data-testid="list-summary" style={{ fontSize: "14px" }}>
              {totalCount === null
                ? "Loading…"
                : filtersActive
                  ? `Showing ${leads.length} of ${totalCount} leads`
                  : `Showing all ${totalCount} leads`}
            </div>
            <div className="flex items-center gap-2">
              {filtersActive && (
                <button
                  onClick={clearFilters}
                  data-testid="list-clear-filters"
                  className="text-xs px-3 py-1.5 rounded-[6px] border border-[#6C47FF] font-[600] text-[#1A1A2E]"
                >
                  Clear filters
                </button>
              )}
              <button
                onClick={() => setShowColumnPicker(true)}
                data-testid="edit-columns-btn"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-[6px] border border-[#6C47FF] font-[600] text-[#1A1A2E] bg-white"
              >
                <Columns3 className="h-3.5 w-3.5" /> Edit Columns
              </button>
            </div>
          </div>
        <div className="bg-white border border-[#E8EAEF] rounded-[12px] shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden" data-testid="leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F0F1F7] border-b border-[#E8EAEF]">
                <tr>
                  {visibleColumns.map((k) => (
                    <th key={k} className="text-left px-4 py-3 text-xs font-[600] text-[#6B7280] uppercase tracking-wider whitespace-nowrap">{COL_LABEL[k]}</th>
                  ))}
                  <th className="text-left px-4 py-3 text-xs font-[600] text-[#6B7280] uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => (
                  <tr
                    key={l.id}
                    onClick={() => openLeadInNewTab(l)}
                    data-testid={`lead-row-${l.id}`}
                    className={`cursor-pointer hover:bg-[#F0F1F7] ${i % 2 === 1 ? "bg-[#F0F1F7]" : "bg-white"} border-b border-[#E8EAEF]`}
                  >
                    {visibleColumns.map((k) => {
                      let content = "—";
                      if (k === "name") content = <span className="font-[600]">{l.name}</span>;
                      else if (k === "category") content = <Pill color={CATEGORY_COLORS[l.category]}>{l.category}</Pill>;
                      else if (k === "stage") content = <Pill color={STAGE_COLORS[l.stage]}>{l.stage}</Pill>;
                      else if (k === "course_interest") content = l.course_interest || "—";
                      else if (k === "source") content = <SourceBadge source={l.source} />;
                      else if (k === "created_at") content = <span className="whitespace-nowrap">{formatDateIST(l.created_at)}</span>;
                      else if (k === "next_followup_at") content = l.next_followup_at ? (
                        <span className={`whitespace-nowrap ${isOverdue(l.next_followup_at) ? "text-red-700 font-[600]" : "text-[#1A1A2E]"}`}>{formatDateIST(l.next_followup_at)}</span>
                      ) : "—";
                      else if (k === "email") content = l.email ? <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()} className="text-[#6C47FF] hover:underline">{l.email}</a> : "—";
                      else if (k === "phone") content = l.phone ? <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} className="text-[#6C47FF] hover:underline">{l.phone}</a> : "—";
                      else content = l[k] || "—";
                      return <td key={k} className="px-4 py-3 text-[#1A1A2E]">{content}</td>;
                    })}
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {l.stage === "Fees Shared" ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => advanceStage(l, "Enrolled")}
                            data-testid={`row-quick-enrolled-${l.id}`}
                            className="px-2 py-1 rounded-[6px] bg-[#166534] text-white text-[11px] font-[600]"
                          >
                            ✓ Enrol
                          </button>
                          <button
                            onClick={() => advanceStage(l, "Not Interested")}
                            data-testid={`row-quick-notinterested-${l.id}`}
                            className="px-2 py-1 rounded-[6px] border border-[#7f1d1d] text-[#7f1d1d] text-[11px] font-[600]"
                          >
                            ✕ Lost
                          </button>
                        </div>
                      ) : nextStageFor(l.stage) ? (
                        <button
                          onClick={() => advanceStage(l, nextStageFor(l.stage))}
                          data-testid={`row-advance-${l.id}`}
                          title={`Move to ${nextStageFor(l.stage)}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-[6px] border border-[#E8EAEF] bg-white hover:border-[#6C47FF] text-[11px] font-[600] text-[#1A1A2E]"
                        >
                          → {nextStageFor(l.stage)}
                        </button>
                      ) : (
                        <span className="text-xs text-[#6B7280]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td colSpan={visibleColumns.length + 1} className="text-center text-[#6B7280] py-12">No leads match filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}

      <NewLeadModal open={showNew} onClose={() => setShowNew(false)} onCreated={(l) => setLeads((p) => [l, ...p])} />
      <ColumnPicker
        open={showColumnPicker}
        onClose={() => setShowColumnPicker(false)}
        config={columns}
        onChange={setColumns}
      />
    </div>
  );
}
